import { z } from "zod";
// Injection-site rotation values, mirroring the Shotsy site picker.
export const INJECTION_SITES = [
    "abdomen_left",
    "abdomen_right",
    "thigh_left",
    "thigh_right",
    "arm_left",
    "arm_right",
];
// Self-reported GLP-1 injection. Distinct kind from ingested medication.dose
// (see ADR-0016). cadenceDays drives the derived "next dose due" reminder.
export const selfReportGlp1DosePayloadSchema = z
    .object({
    medication: z.string().min(1),
    doseAmount: z.number().positive(),
    doseUnit: z.literal("mg"),
    injectionSite: z.enum(INJECTION_SITES).nullable().optional(),
    note: z.string().nullable().optional(),
    cadenceDays: z.number().min(1).max(90).default(7),
})
    .passthrough();
function finiteOrNull(value) {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}
function secondsToHours(value) {
    const seconds = finiteOrNull(value);
    return seconds === null ? null : Number((seconds / 3600).toFixed(2));
}
export function wearableSleepPayload(input) {
    const hours = secondsToHours(input.durationSeconds);
    if (hours === null)
        return null;
    return {
        hours,
        durationSeconds: finiteOrNull(input.durationSeconds) ?? undefined,
        efficiency: finiteOrNull(input.efficiency),
        qualityScore: finiteOrNull(input.qualityScore),
        isNap: input.isNap ?? false,
        stages: {
            deepHours: secondsToHours(input.deepSleepSeconds),
            lightHours: secondsToHours(input.lightSleepSeconds),
            remHours: secondsToHours(input.remSleepSeconds),
            awakeHours: secondsToHours(input.awakeDurationSeconds),
        },
        wakeEvents: finiteOrNull(input.wakeEvents),
        latencySeconds: finiteOrNull(input.latencySeconds),
        ...(input.hypnogram && input.hypnogram.length > 0
            ? { hypnogram: input.hypnogram.map((s) => ({ at: s.at, level: s.level })) }
            : {}),
    };
}
export function wearableActivityPayload(input) {
    const activeCalories = finiteOrNull(input.activeCalories);
    return {
        steps: finiteOrNull(input.steps),
        distanceMeters: finiteOrNull(input.distanceMeters),
        totalCalories: finiteOrNull(input.totalCalories),
        activeCalories,
        activeEnergyKcal: activeCalories,
        activeSeconds: finiteOrNull(input.activeSeconds),
        vigorousSeconds: finiteOrNull(input.vigorousSeconds),
        moderateSeconds: finiteOrNull(input.moderateSeconds),
        lowIntensitySeconds: finiteOrNull(input.lowIntensitySeconds),
        inactiveSeconds: finiteOrNull(input.inactiveSeconds),
        restSeconds: finiteOrNull(input.restSeconds),
        elevationGainMeters: finiteOrNull(input.elevationGainMeters),
        ...(input.zoneMinutes && input.zoneMinutes.length > 0
            ? { zoneMinutes: input.zoneMinutes.map((m) => finiteOrNull(m)) }
            : {}),
        avgSpeedMetersPerSecond: finiteOrNull(input.avgSpeedMetersPerSecond),
        activityName: input.activityName ?? null,
        activityType: finiteOrNull(input.activityType),
        summaryId: input.summaryId ?? null,
    };
}
export function wearableHrvPayload(input) {
    const value = finiteOrNull(input.rmssdMs);
    if (value === null)
        return null;
    return {
        value,
        rmssd_ms: value,
        baseline_ms: finiteOrNull(input.baselineMs),
        z_score: finiteOrNull(input.zScore),
    };
}
export function wearableRhrPayload(input) {
    const value = finiteOrNull(input.bpm);
    if (value === null)
        return null;
    return { value, bpm: value };
}
export function wearableStressPayload(input) {
    const value = finiteOrNull(input.level);
    const payload = {
        value,
        level: value,
        rating: finiteOrNull(input.rating),
        rest_stress_seconds: finiteOrNull(input.restStressSeconds),
        activity_stress_seconds: finiteOrNull(input.activityStressSeconds),
        high_stress_seconds: finiteOrNull(input.highStressSeconds),
    };
    // A row of nothing is not a reading; anything the device did report is.
    return Object.values(payload).some((v) => v !== null) ? payload : null;
}
export function wearableBpPayload(input) {
    const value = finiteOrNull(input.mmhg);
    if (value === null)
        return null;
    return { value, mmhg: value, unit: "mmHg" };
}
export function wearableWeightPayload(input) {
    const value = finiteOrNull(input.kg);
    if (value === null)
        return null;
    return {
        value,
        kg: value,
        unit: "kg",
        bmi: finiteOrNull(input.bmi),
        bodyFatPercentage: finiteOrNull(input.bodyFatPercentage),
    };
}
export function wearableGlucosePayload(input) {
    const value = finiteOrNull(input.mgdl);
    if (value === null)
        return null;
    return { value, mgdl: value, unit: "mg/dL" };
}
export function nutritionMealPayload(input) {
    const kcal = finiteOrNull(input.kcal);
    return {
        mealType: input.mealType ?? null,
        name: input.name ?? null,
        externalId: input.externalId ?? null,
        kcal,
        calories: kcal,
        proteinG: finiteOrNull(input.proteinG),
        carbsG: finiteOrNull(input.carbsG),
        fatG: finiteOrNull(input.fatG),
        fiberG: finiteOrNull(input.fiberG),
        sugarG: finiteOrNull(input.sugarG),
        sodiumMg: finiteOrNull(input.sodiumMg),
    };
}
export function nutritionSodiumPayload(input) {
    const value = finiteOrNull(input.mg);
    if (value === null)
        return null;
    return { value, mg: value, unit: "mg", sourceKind: input.sourceKind ?? "nutrition" };
}
export function hydrationMlPayload(input) {
    const value = finiteOrNull(input.ml);
    if (value === null)
        return null;
    return { value, ml: value, unit: "ml" };
}
export function selfReportGlp1DosePayload(input) {
    return {
        medication: input.medication,
        doseAmount: input.doseAmount,
        doseUnit: "mg",
        injectionSite: input.injectionSite ?? null,
        note: input.note ?? null,
        cadenceDays: input.cadenceDays ?? 7,
    };
}
//# sourceMappingURL=signal-payloads.js.map